/**
 * Made-to-Measure order details: order and client information, stores,
 * additional notes, validation checkboxes, status logs, then the table of
 * parts.
 */
import { useEffect, useRef, useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCheck,
  faExclamationTriangle,
  faPencilAlt,
  faScroll,
  faStore,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';
import type { Role } from '@/shared/constants';
import { downloadBlob } from '@/shared/lib/download';
import { snackbar } from '@/shared/ui/snackbar';
import {
  fabricCodesQueryOptions,
  fetchOrderPdf,
  modelSizesQueryOptions,
  updateOrderPart,
  uploadOrderPdf,
  usersQueryOptions,
  type Order,
} from '../api';
import { ORDER_TYPES, STATUS_COLORS } from '../order-types';
import { formatOrderTime } from '../logic';
import {
  canEditAdditionalNotes,
  showsClientData,
  sortStatusLogs,
} from '../details-logic';
import { StatusLogs } from '../StatusLogs';
import { useUndoToast } from '../use-undo-toast';
import {
  dmsServiceLabel,
  dmsValidationFields,
  groupParts,
  hasSpecialFabric,
  isDmsAdmin,
  isDmsEditable,
  lessAdvancedPart,
  orderParts,
  partGroups,
  partStatusChangeAllowed,
  purchaseOrderFile,
  validationInfo,
  type DmsField,
  type OrderPart,
} from './dms-logic';
import {
  checkDmsForm,
  patchClient,
  patchFabricCode,
  patchMeasurement,
  patchOrder,
  patchPart,
  patchPartSelected,
  patchProps,
  toSavedDmsOrder,
  toggleValidationField,
  type DmsDraft,
} from './dms-form';
import { DmsPartsTable } from './DmsPartsTable';
import { PartDetailsDialog } from './PartDetailsDialog';

export const PART_RELOAD_DELAY_MS = 2000;

export interface DmsOrderDetailsDialogProps {
  order: Order;
  role: Role;
  userId: string;
  onClose: () => void;
  /** Saves the order, reloading the lists when asked. */
  onSave: (order: Order, reload: boolean) => void;
  onPollingChange: (running: boolean) => void;
  onReload: () => void;
}

const SECTION_ICON = { fontSize: 26, marginRight: 10 } as const;

const withPartStatus = (
  draft: DmsDraft,
  partId: string,
  status: number | null | undefined,
) =>
  patchPart(draft, partId, (item) => ({
    ...item,
    customisationStatusId: status,
  }));

export function DmsOrderDetailsDialog({
  order,
  role,
  userId,
  onClose,
  onSave,
  onPollingChange,
  onReload,
}: Readonly<DmsOrderDetailsDialogProps>) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fabricCodes = useQuery(fabricCodesQueryOptions());
  const modelSizes = useQuery(modelSizesQueryOptions());
  const users = useQuery(usersQueryOptions());
  const undoable = useUndoToast();

  const [draft, setDraft] = useState<DmsDraft>(order);
  const [staged, setStaged] = useState<DmsDraft>(order);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [initialNotes, setInitialNotes] = useState('');
  const [updatingParts, setUpdatingParts] = useState<Record<string, boolean>>(
    {},
  );
  const [printing, setPrinting] = useState(false);
  const [uploaded, setUploaded] = useState({
    shirt: !!order.shirtPdfAttachedToOrder,
    other: !!order.otherPdfAttachedToOrder,
  });
  const [openedPart, setOpenedPart] = useState<OrderPart | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const parts = orderParts(draft);
  const grouped = groupParts(parts);
  const groups = partGroups(grouped);
  const showClient = showsClientData(role);
  const admin = isDmsAdmin(role);
  const editable = (field?: DmsField) =>
    isDmsEditable(role, draft, field, undefined, updatingParts);
  const check = submitted ? checkDmsForm(draft, modelSizes.data) : null;
  const logsPart = lessAdvancedPart(parts);

  const enterEditMode = () => {
    setStaged(draft);
    setIsEditMode(true);
  };
  const cancelEdit = () => {
    setDraft(staged);
    setIsEditMode(false);
  };
  const save = (next: DmsDraft, reload: boolean) => {
    onSave(toSavedDmsOrder(next), reload);
    setStaged(next);
  };
  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    const result = checkDmsForm(draft, modelSizes.data);
    setErrorKey(result.errorKey);
    if (result.errorKey) return;
    save(draft, true);
    setIsEditMode(false);
  };

  const setPartUpdating = (partId: string, updating: boolean) =>
    setUpdatingParts((prev) => ({ ...prev, [partId]: updating }));

  /** Optimistic, undoable, then `PUT customization/:id/part`. */
  const updatePartStatus = async (part: OrderPart, target: number) => {
    if (updatingParts[part.id]) return;
    if (!partStatusChangeAllowed(draft, part, target, isEditMode)) return;
    onPollingChange(false);
    const previous = part.customisationStatusId;
    const setStatus = (status: number | null | undefined) =>
      setDraft((current) => withPartStatus(current, part.id, status));
    setPartUpdating(part.id, true);
    setStatus(target);
    const label = `${part.model.data.productLine ?? ''} ${part.sku || ''} - Status change to ${ORDER_TYPES[target]}`;
    await undoable({
      label,
      onCancel: () => {
        setPartUpdating(part.id, false);
        onPollingChange(true);
        setStatus(previous);
      },
      onConfirm: () => void savePartStatus(part.id, target, previous),
    });
  };

  const savePartStatus = async (
    partId: string,
    target: number,
    previous: number | null | undefined,
  ) => {
    try {
      const data = await updateOrderPart(draft.id, {
        id: partId,
        customisationStatusId: target,
      });
      const saved = orderParts(data).find((item) => item.id === partId);
      const apply = (current: DmsDraft) =>
        patchOrder(saved ? patchPart(current, partId, () => saved) : current, {
          customisationStatusId: data.customisationStatusId,
        });
      setDraft(apply);
      setStaged(apply);
      setUpdatingParts((prev) => {
        const next = { ...prev, [partId]: false };
        if (Object.values(next).every((updating) => !updating))
          setTimeout(() => {
            onReload();
            onPollingChange(true);
          }, PART_RELOAD_DELAY_MS);
        return next;
      });
    } catch (error) {
      console.error(error);
      toast.error('Update failed');
      setDraft((current) => withPartStatus(current, partId, previous));
      setUpdatingParts((prev) => {
        const next = { ...prev, [partId]: false };
        if (Object.values(next).every((updating) => !updating))
          onPollingChange(true);
        return next;
      });
    }
  };

  /** The PDF generated by the API, downloaded. */
  const downloadPdf = async (group: readonly OrderPart[]) => {
    const file = purchaseOrderFile(draft, group);
    setPrinting(true);
    try {
      downloadBlob(await fetchOrderPdf(draft.id, file.type), file.fileName);
    } catch (error) {
      console.error(error);
    } finally {
      setPrinting(false);
    }
  };

  /** The API generates and attaches the PDF. */
  const uploadPdf = async (group: readonly OrderPart[]) => {
    if (printing) return;
    const file = purchaseOrderFile(draft, group);
    setPrinting(true);
    try {
      await uploadOrderPdf(draft.id, file.uploadType);
      snackbar('Successfully uploaded');
      setUploaded((prev) => ({ ...prev, [file.type]: true }));
    } catch (error) {
      snackbar('Failed to upload');
      console.error(error);
    } finally {
      setPrinting(false);
    }
  };

  const onPartField = (partId: string, field: string, value: string) => {
    if (field === 'selected.size' || field === 'selected.sizePants') {
      const key = field.slice('selected.'.length);
      setDraft((current) =>
        patchPartSelected(current, partId, { [key]: value }),
      );
      return;
    }
    setDraft((current) =>
      patchPart(current, partId, (item) => ({ ...item, [field]: value })),
    );
  };

  const pencil = (
    field: DmsField | undefined,
    label: string,
    testId?: string,
  ) =>
    editable(field) && (
      <button
        type="button"
        className="pencil"
        aria-label={`Edit ${label}`}
        data-testid={testId}
        onClick={enterEditMode}
      >
        <FontAwesomeIcon icon={faPencilAlt} />
      </button>
    );

  const textField = (
    label: string,
    name: string,
    value: string,
    onChange: (value: string) => void,
    options: {
      field?: DmsField;
      type?: string;
      placeholder?: string;
      invalid?: boolean;
      display?: string;
      testId?: string;
      editButtonTestId?: string;
    } = {},
  ) => (
    <div className="flex" data-testid={options.testId}>
      <span className="modal-label">
        {label}{' '}
        {pencil(
          options.field,
          label,
          options.editButtonTestId ??
            (options.testId ? `${options.testId}-edit-button` : undefined),
        )}
        :
      </span>
      <span className="view-item">{options.display ?? value}</span>
      <input
        type={options.type ?? 'text'}
        className={`form-control edit-item ${options.invalid ? 'error' : ''}`}
        name={name}
        aria-label={label}
        placeholder={options.placeholder}
        data-testid={options.testId ? `${options.testId}-input` : undefined}
        readOnly={!editable(options.field)}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );

  const props = (draft.props ?? {}) as Record<string, unknown>;
  const client = (draft.client ?? {}) as Record<string, unknown>;
  const product = draft.product ?? {};
  const text = (value: unknown) => (value == null ? '' : String(value));

  return (
    <dialog
      ref={dialogRef}
      className="app-dialog order-details-dialog dms-details-dialog"
      aria-label={`ORDER - ${draft.displayId ?? ''}`}
      data-testid="mtm-order-details-dialog"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div
        className={`order-details dms-details ${isEditMode ? 'edit-mode' : 'view-mode'}`}
      >
        <div className="px-6 py-4 w-full">
          <div className="flex justify-between items-center mt-4 mb-2 relative">
            <button
              type="button"
              className="close p-2 dms-close"
              aria-label="Close"
              data-testid="mtm-order-details-close-button"
              onClick={onClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <div className="details-modal-header">
              ORDER - {draft.displayId}
            </div>
            <div className="flex items-center">
              {!!props.isExpress && (
                <div className="flex items-center" style={{ marginRight: 20 }}>
                  {t('orders.expressOption')}
                  <FontAwesomeIcon
                    icon={faBolt}
                    style={{ marginLeft: 5, fontSize: '1.5em' }}
                  />
                </div>
              )}
              <div className="styled-input-square inline-flex">
                <div
                  className="styled-input-single"
                  style={{ marginRight: 10 }}
                >
                  <input
                    type="checkbox"
                    id={`onhold-${draft.id}`}
                    name="onHold"
                    data-testid="order-details-hold-toggle"
                    checked={!!draft.onHold}
                    onChange={(event) => {
                      const next = patchOrder(draft, {
                        onHold: event.target.checked,
                      });
                      setDraft(next);
                      save(next, false);
                    }}
                  />
                  <label htmlFor={`onhold-${draft.id}`} style={{ margin: 0 }}>
                    {t('orders.personalisationOnHold')}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} noValidate style={{ width: '100%' }}>
            <div className="bs-row">
              <div className="col-span-6">
                <div className="bs-row mt-2">
                  <div className="col-span-6">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faScroll} style={SECTION_ICON} />
                      <div className="section-header">
                        {t('orders.orderInfo')}
                      </div>
                    </div>
                    <div className="flex" data-testid="order-details-status">
                      <span className="modal-label">{t('orders.status')}:</span>
                      <span
                        style={{
                          color: STATUS_COLORS[draft.customisationStatusId],
                        }}
                      >
                        {ORDER_TYPES[draft.customisationStatusId]}
                      </span>
                      {draft.onHold && (
                        <span className="text-danger ml-2">
                          (<FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                          {t('orders.onHold')})
                        </span>
                      )}
                    </div>
                    <div className="flex" data-testid="order-details-date">
                      <span className="modal-label">{t('orders.date')}:</span>
                      <span>{formatOrderTime(draft, 'MMMM D, YYYY')}</span>
                    </div>
                    <div
                      className="flex"
                      data-testid="mtm-order-details-express"
                    >
                      <span className="modal-label">
                        {t('orders.express')}:
                      </span>
                      <span>{props.isExpress ? 'YES' : 'NO'}</span>
                    </div>
                    {textField(
                      t('orders.orderId'),
                      'externalOrderId',
                      draft.externalOrderId ?? '',
                      (value) =>
                        setDraft(patchOrder(draft, { externalOrderId: value })),
                      {
                        field: 'externalOrderId',
                        placeholder: t('orders.orderId'),
                        invalid: !!check?.orderIdMissing,
                        testId: 'order-details-transaction-id',
                        editButtonTestId:
                          'order-details-transaction-id-edit-button',
                      },
                    )}
                    {textField(
                      t('orders.deposit'),
                      'props.deposit',
                      text(props.deposit),
                      (value) =>
                        setDraft(patchProps(draft, { deposit: value })),
                      { testId: 'mtm-order-details-deposit' },
                    )}
                    {textField(
                      t('orders.tailor'),
                      'props.tailor',
                      text(props.tailor),
                      (value) => setDraft(patchProps(draft, { tailor: value })),
                      { testId: 'mtm-order-details-tailor' },
                    )}
                    {textField(
                      t('orders.suitSpecialist'),
                      'props.suitSpecialist',
                      text(props.suitSpecialist),
                      (value) =>
                        setDraft(patchProps(draft, { suitSpecialist: value })),
                      { testId: 'mtm-order-details-suit-specialist' },
                    )}
                    <div
                      className="flex"
                      data-testid="mtm-order-details-service"
                    >
                      <span className="modal-label">
                        {t('orders.service')}:
                      </span>
                      <span>{dmsServiceLabel(draft)}</span>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUser} style={SECTION_ICON} />
                      <div className="section-header">{t('orders.client')}</div>
                    </div>
                    {textField(
                      t('orders.firstName'),
                      'firstName',
                      draft.firstName ?? '',
                      (value) =>
                        setDraft(patchOrder(draft, { firstName: value })),
                      { testId: 'order-details-client-first-name' },
                    )}
                    {textField(
                      t('orders.lastName'),
                      'lastName',
                      draft.lastName ?? '',
                      (value) =>
                        setDraft(patchOrder(draft, { lastName: value })),
                      { testId: 'order-details-client-last-name' },
                    )}
                    {textField(
                      t('orders.email'),
                      'clientEmail',
                      draft.clientEmail ?? '',
                      (value) =>
                        setDraft(patchOrder(draft, { clientEmail: value })),
                      {
                        type: 'email',
                        display: draft.clientEmail || ' - ',
                        testId: 'mtm-order-details-email',
                      },
                    )}
                    {textField(
                      t('orders.phone'),
                      'clientPhone',
                      draft.clientPhone ?? '',
                      (value) =>
                        setDraft(patchOrder(draft, { clientPhone: value })),
                      { testId: 'mtm-order-details-phone' },
                    )}
                    {textField(
                      t('orders.language'),
                      'language',
                      text(client.language),
                      (value) =>
                        setDraft(patchClient(draft, { language: value })),
                      { testId: 'mtm-order-details-language' },
                    )}
                    <div className="flex" data-testid="order-details-client-id">
                      <span className="modal-label">
                        {t('orders.clientId')}{' '}
                        {showClient &&
                          pencil(
                            'externalClientId',
                            t('orders.clientId'),
                            'order-details-client-id-edit-button',
                          )}
                        :
                      </span>
                      <span className="view-item">
                        {showClient ? draft.externalClientId : 'XXX'}
                      </span>
                      {showClient && editable('externalClientId') && (
                        <input
                          type="text"
                          className="form-control edit-item"
                          name="externalClientId"
                          aria-label={t('orders.clientId')}
                          placeholder={t('orders.clientId')}
                          data-testid="order-details-client-id-input"
                          value={draft.externalClientId ?? ''}
                          onChange={(event) =>
                            setDraft(
                              patchOrder(draft, {
                                externalClientId: event.target.value,
                              }),
                            )
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faStore} style={SECTION_ICON} />
                    <div className="section-header">{t('orders.stores')}</div>
                  </div>
                  <div className="flex" data-testid="order-details-store">
                    <span className="modal-label">
                      {t('orders.purchaseStore')}:
                    </span>
                    <span>
                      {draft.storeCode} - {draft.storeName}
                    </span>
                  </div>
                  {textField(
                    t('orders.deliveryStore'),
                    'deliveryStore',
                    text(props.deliveryStore),
                    (value) =>
                      setDraft(patchProps(draft, { deliveryStore: value })),
                    {
                      field: 'deliveryStore',
                      testId: 'mtm-order-details-delivery-store',
                    },
                  )}
                  <div data-testid="mtm-order-details-direct-delivery">
                    <span className="modal-label">
                      {t('orders.directDeliveryInStore')}{' '}
                      {admin && (
                        <button
                          type="button"
                          className="pencil"
                          aria-label={`Edit ${t('orders.directDeliveryInStore')}`}
                          data-testid="mtm-order-details-direct-delivery-edit-button"
                          onClick={enterEditMode}
                        >
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                      )}
                      :
                    </span>
                    <div className="styled-input-square inline-flex">
                      <div
                        className="styled-input-single"
                        style={{ marginRight: 10 }}
                      >
                        <input
                          type="checkbox"
                          id={`directDelivery-${draft.id}`}
                          name="directDeliveryInStore"
                          data-testid="mtm-order-details-direct-delivery-toggle"
                          checked={!!props.directDeliveryInStore}
                          disabled={!isEditMode || !admin}
                          onChange={(event) =>
                            setDraft(
                              patchProps(draft, {
                                directDeliveryInStore: event.target.checked,
                              }),
                            )
                          }
                        />
                        <label
                          htmlFor={`directDelivery-${draft.id}`}
                          style={{ marginBottom: 0 }}
                          className={
                            !isEditMode || !admin ? 'text-disabled' : ''
                          }
                        >
                          <span className="sr-only">
                            {t('orders.directDeliveryInStore')}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-3">
                <label className="modal-label" htmlFor={`notes-${draft.id}`}>
                  {t('orders.additionalNotes')}
                </label>
                <br />
                <textarea
                  id={`notes-${draft.id}`}
                  cols={30}
                  rows={5}
                  className="form-control modal-textarea"
                  data-testid="order-details-notes-textbox"
                  value={draft.notes ?? ''}
                  disabled={!notesEditMode}
                  onChange={(event) =>
                    setDraft(patchOrder(draft, { notes: event.target.value }))
                  }
                />
                {notesEditMode ? (
                  <>
                    <button
                      key="notes-save"
                      type="button"
                      className="btn btn-dark modal-button short"
                      data-testid="order-details-notes-save-button"
                      onClick={() => {
                        save(draft, false);
                        setNotesEditMode(false);
                      }}
                    >
                      {t('orders.save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-dark modal-button short mt-1 mb-4"
                      data-testid="order-details-notes-cancel-button"
                      onClick={() => {
                        setDraft(patchOrder(draft, { notes: initialNotes }));
                        setNotesEditMode(false);
                      }}
                    >
                      {t('orders.cancelOrder')}
                    </button>
                  </>
                ) : (
                  canEditAdditionalNotes(role, isEditMode) && (
                    <button
                      key="notes-edit"
                      type="button"
                      className="btn btn-dark modal-button short mb-4"
                      data-testid="order-details-notes-edit-button"
                      onClick={() => {
                        setInitialNotes(draft.notes ?? '');
                        setNotesEditMode(true);
                      }}
                    >
                      {t('orders.edit')}
                    </button>
                  )
                )}
                <div className="mt-2">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faCheck} style={SECTION_ICON} />
                    <div className="section-header">
                      {t('orders.validation')}
                    </div>
                  </div>
                  <div data-testid="mtm-order-details-validation">
                    {dmsValidationFields(parts).map((field) => {
                      const allowed = editable(`product.${field}`);
                      const info = validationInfo(
                        product[`${field}Checked`] as
                          { user?: unknown; date?: unknown } | null | undefined,
                        users.data,
                      );
                      return (
                        <div key={field}>
                          <div className="styled-input-square">
                            <div
                              className="styled-input-single"
                              style={{ marginRight: 10 }}
                            >
                              <input
                                type="checkbox"
                                id={`validation-${field}-${draft.id}`}
                                name={field}
                                data-testid={`mtm-order-details-validation-${field}`}
                                checked={!!product[field]}
                                disabled={!isEditMode || !allowed}
                                onChange={(event) =>
                                  setDraft(
                                    toggleValidationField(
                                      draft,
                                      field,
                                      event.target.checked,
                                      userId,
                                    ),
                                  )
                                }
                              />
                              <label
                                htmlFor={`validation-${field}-${draft.id}`}
                                className={allowed ? '' : 'text-disabled'}
                              >
                                {t(`orders.${field}`)}
                              </label>
                            </div>
                          </div>
                          {info && (
                            <div>
                              <i
                                className="text-ellipsis validation-info"
                                title={info}
                                data-testid={`mtm-order-details-validation-${field}-info`}
                              >
                                {info}
                              </i>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="col-span-3">
                <div
                  className="mt-2 mb-2 status-logs"
                  style={{ height: 320 }}
                  data-testid="mtm-order-details-timeline"
                >
                  <StatusLogs logs={sortStatusLogs(logsPart?.statusLogs)} />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <DmsPartsTable
                order={draft}
                role={role}
                groups={groups}
                isEditMode={isEditMode}
                updatingParts={updatingParts}
                invalidParts={check?.invalidParts ?? new Set()}
                fabricCodes={fabricCodes.data}
                printing={printing}
                uploadDisabled={(group) =>
                  uploaded[purchaseOrderFile(draft, group).type]
                }
                onEnterEdit={enterEditMode}
                onPartField={onPartField}
                onPartFlag={(partId, field, value) =>
                  setDraft((current) =>
                    patchPart(current, partId, (item) => ({
                      ...item,
                      [field]: value,
                    })),
                  )
                }
                onFabric={(partId, code) =>
                  setDraft((current) => {
                    const part = orderParts(current).find(
                      (item) => item.id === partId,
                    );
                    return part
                      ? patchFabricCode(
                          current,
                          partId,
                          code,
                          hasSpecialFabric(part),
                        )
                      : current;
                  })
                }
                onMeasurement={(partId, index, patch) =>
                  setDraft((current) =>
                    patchMeasurement(current, partId, index, patch),
                  )
                }
                onPartStatus={(part, target) =>
                  void updatePartStatus(part, target)
                }
                onOpenPart={setOpenedPart}
                onDownloadPdf={(group) => void downloadPdf(group)}
                onUploadPdf={(group) => void uploadPdf(group)}
              />
              {errorKey && (
                <div
                  className="mt-4 alert-danger text-center"
                  role="alert"
                  data-testid="order-details-error"
                >
                  {t(errorKey)}
                </div>
              )}
              <div className="flex items-center justify-center mt-4">
                {editable('any') &&
                  (isEditMode ? (
                    <>
                      <button
                        key="save"
                        type="submit"
                        className="btn btn-dark modal-button narrow"
                        data-testid="order-details-save-button"
                      >
                        {t('orders.save')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-dark modal-button narrow ml-2"
                        data-testid="order-details-cancel-button"
                        onClick={cancelEdit}
                      >
                        {t('orders.cancelOrder')}
                      </button>
                    </>
                  ) : (
                    <button
                      key="edit"
                      type="button"
                      className="btn btn-dark modal-button narrow"
                      data-testid="order-details-edit-button"
                      onClick={enterEditMode}
                    >
                      {t('orders.edit')}
                    </button>
                  ))}
              </div>
            </div>
          </form>
        </div>
      </div>
      {openedPart && (
        <PartDetailsDialog
          part={openedPart}
          onClose={() => setOpenedPart(null)}
        />
      )}
    </dialog>
  );
}
